import { useState } from 'react'
import { API, graphqlOperation } from 'aws-amplify'
import { HomeIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import { v4 as uuidv4 } from 'uuid'

import { mutation } from '../api'
import Button from '../components/Button'
import { useNotificationContext } from '../context/notification-context'
import { getCareer, getPartner, careerAnalyseByCareer } from '../api/queries'

const pages = [
  { name: 'System', to: '/system', current: false },
  { name: 'Stellen kopieren', to: '#', current: true }
]

const validationSchema = Yup.object().shape({
  career_id: Yup.string().required('Stellen ID ist erforderlich'),
  target_partner_ids: Yup.string().required(
    'Mindestens eine Ziel Partner ID ist erforderlich'
  )
})

const CopyPosition = () => {
  const { success, error } = useNotificationContext()
  const [careerData, setCareerData] = useState(null)
  const [partnersData, setPartnersData] = useState([])
  const [careerAnalyses, setCareerAnalyses] = useState([])
  const [isLoadingAnalyses, setIsLoadingAnalyses] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [copyComplete, setCopyComplete] = useState(false)
  const [copyStats, setCopyStats] = useState({
    positions: 0,
    totalCareerAnalyses: 0,
    totalPersons: 0,
    partnerResults: []
  })
  const [exportData, setExportData] = useState(null)

  const fetchCareer = async (career_id) => {
    try {
      // Check if career_id contains "-p-" which indicates it's from an ATS
      if (career_id.includes('-p-')) {
        error('Diese Stelle stammt aus einem ATS und kann nicht kopiert werden')
        setCareerData(null)
        setCareerAnalyses([])
        return null
      }

      const res = await API.graphql(
        graphqlOperation(getCareer, { id: career_id })
      )

      setCareerData(res.data.getCareer)

      // Fetch related CareerAnalyses
      await fetchCareerAnalyses(career_id)

      return res.data.getCareer
    } catch (err) {
      // Log detailed error information for debugging
      const errorDetails = {
        message: err.message,
        errors: err.errors,
        data: err.data
      }

      error(
        `Fehler beim Laden der Stelle: ${err.message || 'Unbekannter Fehler'}. Details: ${JSON.stringify(errorDetails)}`
      )
      setCareerData(null)
      setCareerAnalyses([])
      return null
    }
  }

  const fetchCareerAnalyses = async (career_id) => {
    setIsLoadingAnalyses(true)
    try {
      const result = await API.graphql(
        graphqlOperation(careerAnalyseByCareer, {
          career_id,
          limit: 1000
        })
      )

      const analyses = result.data.careerAnalyseByCareer.items || []
      setCareerAnalyses(analyses)
      return analyses
    } catch (err) {
      error(
        `Fehler beim Laden der CareerAnalysen: ${err.message || 'Unbekannter Fehler'}`
      )
      setCareerAnalyses([])
      return []
    } finally {
      setIsLoadingAnalyses(false)
    }
  }

  const fetchPartners = async (partner_ids_string) => {
    try {
      const partner_ids = partner_ids_string
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id)
      const partners = []

      for (const partner_id of partner_ids) {
        const res = await API.graphql(
          graphqlOperation(getPartner, { id: partner_id })
        )
        if (res.data.getPartner) {
          partners.push(res.data.getPartner)
        }
      }

      setPartnersData(partners)
      return partners
    } catch (err) {
      error(
        `Fehler beim Laden der Partner: ${err.message || 'Unbekannter Fehler'}`
      )
      setPartnersData([])
      return []
    }
  }

  const generateExportData = (
    career,
    analyses,
    oldPartnerId,
    newPartnerId,
    newCareerID,
    personIdMapping = {}
  ) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

    // Extract unique persons from analyses
    const uniquePersons = new Map()
    analyses.forEach((analysis) => {
      if (
        analysis.person_id &&
        analysis.person &&
        !uniquePersons.has(analysis.person_id)
      ) {
        uniquePersons.set(analysis.person_id, analysis.person)
      }
    })

    const exportObj = {
      timestamp,
      career: {
        original: {
          ...career,
          partner_id: oldPartnerId
        },
        copy: {
          ...career,
          id: newCareerID,
          partner_id: newPartnerId,
          copy_from_career_id: career.id
        }
      },
      persons: Array.from(uniquePersons.entries()).map(
        ([oldPersonId, person]) => ({
          original: {
            ...person,
            partner_id: oldPartnerId
          },
          copy: {
            ...person,
            id: personIdMapping[oldPersonId] || uuidv4(), // Use actual mapping or generate for export
            partner_id: newPartnerId,
            copy_from_person_id: oldPersonId
          }
        })
      ),
      careerAnalyses: analyses.map((analysis) => ({
        original: {
          ...analysis,
          partner_id: oldPartnerId,
          career_id: career.id
        },
        copy: {
          ...analysis,
          id: uuidv4(), // This is just for the export, not the actual ID used
          partner_id: newPartnerId,
          career_id: newCareerID,
          person_id: personIdMapping[analysis.person_id] || analysis.person_id,
          copy_from_careeranalyse_id: analysis.id,
          copy_from_careeranalyse_career_id: career.id
        }
      }))
    }

    return {
      data: exportObj,
      filename: `position-copy-${career.id}-${timestamp}.json`
    }
  }

  const downloadExportData = () => {
    if (!exportData) return

    const dataStr = JSON.stringify(exportData.data, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)

    const a = document.createElement('a')
    a.href = url
    a.download = exportData.filename
    document.body.appendChild(a)
    a.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 100)
  }

  const copyPositionToSinglePartner = async (
    career,
    partner,
    careerAnalyses
  ) => {
    // Update authorization fields by replacing old partner_id with new partner_id
    const updateAuthFields = (authArray, oldPartnerId, newPartnerId) => {
      if (!authArray || !Array.isArray(authArray)) return []

      return authArray.map((item) => {
        if (item.includes(oldPartnerId)) {
          return item.replace(oldPartnerId, newPartnerId)
        }
        return item
      })
    }

    // 1. Create a new career entity with the new partner_id and authorization fields
    // Get current authorization fields
    const careerAuthFields = {
      authorized_create: career.authorized_create || [],
      authorized_read: career.authorized_read || [],
      authorized_update: career.authorized_update || [],
      authorized_delete: career.authorized_delete || []
    }

    // Create a new ID for the copied career
    const newCareerID = uuidv4()

    // Create a new career entity as a copy of the original
    const careerCreateInput = {
      id: newCareerID,
      name: career.name,
      title: career.title,
      status: career.status,
      archived: career.archived,
      is_available_in_attract: career.is_available_in_attract,
      was_seen_times_in_attract_funnel: career.was_seen_times_in_attract_funnel,
      logo: career.logo,
      photo_url: career.photo_url,
      language: career.language,
      category: career.category,
      diagnostic_version: career.diagnostic_version,
      partner_id: partner.id,
      partner_name: partner.name || partner.display_name,
      external_link: career.external_link,
      related_career: career.related_career,
      riasec_id: career.riasec_id,
      R: career.R,
      I: career.I,
      A: career.A,
      S: career.S,
      E: career.E,
      C: career.C,
      app_settings: career.app_settings,
      forced_dimension: career.forced_dimension,
      challenge_config_id: career.challenge_config_id,
      career_analyse: career.career_analyse,
      redirect_url: career.redirect_url,
      copy_from_career_id: career.id,
      external_custom_id: career.external_custom_id,
      authorized_create: updateAuthFields(
        careerAuthFields.authorized_create,
        career.partner_id,
        partner.id
      ),
      authorized_read: updateAuthFields(
        careerAuthFields.authorized_read,
        career.partner_id,
        partner.id
      ),
      authorized_update: updateAuthFields(
        careerAuthFields.authorized_update,
        career.partner_id,
        partner.id
      ),
      authorized_delete: updateAuthFields(
        careerAuthFields.authorized_delete,
        career.partner_id,
        partner.id
      )
    }

    await mutation({
      mutation: 'createCareer',
      input: careerCreateInput
    })

    // 2. Create a mapping of old person_id to new person_id by copying persons
    const personIdMapping = {}
    const uniquePersons = new Map()

    // Collect unique persons from all analyses
    for (const careerAnalyse of careerAnalyses) {
      if (
        careerAnalyse.person_id &&
        careerAnalyse.person &&
        !uniquePersons.has(careerAnalyse.person_id)
      ) {
        uniquePersons.set(careerAnalyse.person_id, careerAnalyse.person)
      }
    }

    // Copy each unique person to the new partner
    for (const [oldPersonId, person] of uniquePersons) {
      const newPersonId = uuidv4()

      // Get current authorization fields for this Person
      const personAuthFields = {
        authorized_create: person.authorized_create || [],
        authorized_read: person.authorized_read || [],
        authorized_update: person.authorized_update || [],
        authorized_delete: person.authorized_delete || []
      }

      // Create a new Person as a copy of the original
      await mutation({
        mutation: 'createPerson',
        input: {
          id: newPersonId,
          owner: person.owner,
          language: person.language,
          invitation_firstname: person.invitation_firstname,
          firstname: person.firstname,
          invitation_lastname: person.invitation_lastname,
          lastname: person.lastname,
          invitation_email: person.invitation_email,
          email: person.email,
          phone: person.phone,
          postcode: person.postcode,
          city: person.city,
          partner_id: partner.id,
          lastUpdatedBy: person.lastUpdatedBy,
          personSpaceId: person.personSpaceId,
          authorized_create: updateAuthFields(
            personAuthFields.authorized_create,
            career.partner_id,
            partner.id
          ),
          authorized_read: updateAuthFields(
            personAuthFields.authorized_read,
            career.partner_id,
            partner.id
          ),
          authorized_update: updateAuthFields(
            personAuthFields.authorized_update,
            career.partner_id,
            partner.id
          ),
          authorized_delete: updateAuthFields(
            personAuthFields.authorized_delete,
            career.partner_id,
            partner.id
          )
        }
      })

      // Store the mapping for later use
      personIdMapping[oldPersonId] = newPersonId
    }

    // 3. Create a copy of each CareerAnalyse with the new career_id, partner_id, and person_id
    let copiedAnalysesCount = 0
    for (const careerAnalyse of careerAnalyses) {
      // Get current authorization fields for this CareerAnalyse
      const analyseAuthFields = {
        authorized_create: careerAnalyse.authorized_create || [],
        authorized_read: careerAnalyse.authorized_read || [],
        authorized_update: careerAnalyse.authorized_update || [],
        authorized_delete: careerAnalyse.authorized_delete || []
      }

      // Create a new ID for the copied CareerAnalyse
      const newCareerAnalyseID = uuidv4()

      // Get the new person_id from the mapping
      const newPersonId =
        personIdMapping[careerAnalyse.person_id] || careerAnalyse.person_id

      // Create a new CareerAnalyse as a copy of the original
      await mutation({
        mutation: 'createCareerAnalyse',
        input: {
          id: newCareerAnalyseID,
          input: careerAnalyse.input,
          diagnostic_version: careerAnalyse.diagnostic_version,
          preset: careerAnalyse.preset,
          time_needed: careerAnalyse.time_needed,
          verified: careerAnalyse.verified,
          status: careerAnalyse.status,
          career_id: newCareerID, // Link to the new career
          partner_id: partner.id,
          language: careerAnalyse.language,
          person_id: newPersonId, // Link to the new person
          url_params: careerAnalyse.url_params,
          invited_from_user_id: careerAnalyse.invited_from_user_id,
          total_time_needed_in_min: careerAnalyse.total_time_needed_in_min,
          copy_from_careeranalyse_id: careerAnalyse.id,
          copy_from_careeranalyse_career_id: career.id,
          authorized_create: updateAuthFields(
            analyseAuthFields.authorized_create,
            career.partner_id,
            partner.id
          ),
          authorized_read: updateAuthFields(
            analyseAuthFields.authorized_read,
            career.partner_id,
            partner.id
          ),
          authorized_update: updateAuthFields(
            analyseAuthFields.authorized_update,
            career.partner_id,
            partner.id
          ),
          authorized_delete: updateAuthFields(
            analyseAuthFields.authorized_delete,
            career.partner_id,
            partner.id
          )
        }
      })
      copiedAnalysesCount++
    }

    return {
      partnerId: partner.id,
      partnerName: partner.name || partner.display_name,
      newCareerID,
      copiedAnalyses: copiedAnalysesCount,
      copiedPersons: uniquePersons.size,
      personIdMapping
    }
  }

  const copyPosition = async (values) => {
    setIsCopying(true)
    setCopyComplete(false)
    setCopyStats({
      positions: 0,
      totalCareerAnalyses: 0,
      totalPersons: 0,
      partnerResults: []
    })

    try {
      // Fetch the career and partners data to make sure they exist
      const career = await fetchCareer(values.career_id)
      const partners = await fetchPartners(values.target_partner_ids)

      if (!career || !partners || partners.length === 0) {
        setIsCopying(false)
        return
      }

      // Find all CareerAnalyses related to this career
      const careerAnalysesResult = await API.graphql(
        graphqlOperation(careerAnalyseByCareer, {
          career_id: career.id,
          limit: 1000
        })
      )

      const careerAnalyses =
        careerAnalysesResult.data.careerAnalyseByCareer.items || []

      // Copy position to each partner
      const partnerResults = []
      let totalCareerAnalyses = 0
      let totalPersons = 0

      for (const partner of partners) {
        try {
          const result = await copyPositionToSinglePartner(
            career,
            partner,
            careerAnalyses
          )
          partnerResults.push(result)
          totalCareerAnalyses += result.copiedAnalyses
          totalPersons += result.copiedPersons
        } catch (err) {
          error(
            `Fehler beim Kopieren zu Partner ${partner.name || partner.id}: ${err.message || 'Unbekannter Fehler'}`
          )
          partnerResults.push({
            partnerId: partner.id,
            partnerName: partner.name || partner.display_name,
            error: err.message || 'Unbekannter Fehler'
          })
        }
      }

      setCopyStats({
        positions: partners.length,
        totalCareerAnalyses,
        totalPersons,
        partnerResults
      })

      // Generate export data for backup (using first partner for now)
      if (partnerResults.length > 0 && partnerResults[0].newCareerID) {
        const exportDataObj = generateExportData(
          career,
          careerAnalyses,
          career.partner_id,
          partnerResults[0].partnerId,
          partnerResults[0].newCareerID,
          partnerResults[0].personIdMapping || {}
        )
        setExportData(exportDataObj)
      }

      setCopyComplete(true)
      success(`Stelle erfolgreich zu ${partners.length} Partner(n) kopiert`)
    } catch (err) {
      error(
        `Fehler beim Kopieren der Stelle: ${err.message || 'Unbekannter Fehler'}`
      )
    } finally {
      setIsCopying(false)
    }
  }

  return (
    <div>
      <nav className='flex mb-8' aria-label='Breadcrumb'>
        <ol className='flex items-center space-x-4'>
          <li>
            <div>
              <Link to='/' className='text-gray-400 hover:text-gray-500'>
                <HomeIcon
                  className='h-5 w-5 flex-shrink-0'
                  aria-hidden='true'
                />
                <span className='sr-only'>Home</span>
              </Link>
            </div>
          </li>
          {pages.map((page) => (
            <li key={page.name}>
              <div className='flex items-center'>
                <svg
                  className='h-5 w-5 flex-shrink-0 text-gray-300'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                  aria-hidden='true'
                >
                  <path d='M5.555 17.776l8-16 .894.448-8 16-.894-.448z' />
                </svg>
                <Link
                  to={page.to}
                  className={`ml-4 text-sm font-medium ${
                    page.current
                      ? 'text-indigo-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  aria-current={page.current ? 'page' : undefined}
                >
                  {page.name}
                </Link>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      <header className='mb-8'>
        <h1 className='text-3xl font-bold leading-tight tracking-tight text-gray-900'>
          Stellen kopieren
        </h1>
        <p className='mt-2 text-sm text-gray-700'>
          Hier können Sie eine Stelle von einem Partner zu mehreren anderen
          Partnern kopieren. Dabei werden auch alle zugehörigen CareerAnalysen
          und deren Personen mit kopiert. Die Stelle bleibt beim ursprünglichen
          Partner erhalten. Geben Sie mehrere Partner-IDs kommagetrennt ein.
        </p>
      </header>

      <div className='bg-white shadow sm:rounded-lg'>
        <div className='px-4 py-5 sm:p-6'>
          <Formik
            initialValues={{
              career_id: '',
              target_partner_ids: ''
            }}
            validationSchema={validationSchema}
            onSubmit={copyPosition}
          >
            {({ errors, touched, values, isSubmitting }) => (
              <Form className='space-y-6'>
                <div>
                  <label
                    htmlFor='career_id'
                    className='block text-sm font-medium leading-6 text-gray-900'
                  >
                    Stellen ID
                  </label>
                  <div className='mt-2 flex'>
                    <Field
                      type='text'
                      name='career_id'
                      id='career_id'
                      className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3'
                      placeholder='Stellen ID eingeben'
                    />
                    <Button.PrimaryLG
                      text='Laden'
                      className='ml-4'
                      onClick={() => fetchCareer(values.career_id)}
                      disabled={
                        !values.career_id || values.career_id.includes('-p-')
                      }
                    />
                  </div>
                  {errors.career_id && touched.career_id ? (
                    <div className='text-sm text-red-600 mt-1'>
                      {errors.career_id}
                    </div>
                  ) : values.career_id && values.career_id.includes('-p-') ? (
                    <div className='text-sm text-red-600 mt-1'>
                      Diese Stelle stammt aus einem ATS und kann nicht kopiert
                      werden
                    </div>
                  ) : null}
                </div>

                {careerData && (
                  <div className='bg-gray-50 p-4 rounded-md'>
                    <h3 className='text-base font-semibold leading-6 text-gray-900'>
                      Stellen Details
                    </h3>
                    <dl className='mt-2 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2'>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {careerData.id}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {careerData.name}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Aktueller Partner ID
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {careerData.partner_id}
                        </dd>
                      </div>
                      <div className='sm:col-span-1'>
                        <dt className='text-sm font-medium text-gray-500'>
                          Partner Name
                        </dt>
                        <dd className='mt-1 text-sm text-gray-900'>
                          {careerData.partner_name}
                        </dd>
                      </div>
                    </dl>

                    <div className='mt-6'>
                      <h4 className='text-sm font-semibold text-gray-900'>
                        Zugehörige CareerAnalysen ({careerAnalyses.length})
                      </h4>
                      {isLoadingAnalyses ? (
                        <div className='mt-2 flex justify-center'>
                          <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500'></div>
                        </div>
                      ) : careerAnalyses.length > 0 ? (
                        <div className='mt-2 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg'>
                          <table className='min-w-full divide-y divide-gray-300'>
                            <thead className='bg-gray-50'>
                              <tr>
                                <th
                                  scope='col'
                                  className='py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500 sm:pl-6'
                                >
                                  Person
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Status
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Sprache
                                </th>
                                <th
                                  scope='col'
                                  className='px-3 py-2 text-left text-xs font-medium text-gray-500'
                                >
                                  Erstellt
                                </th>
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-200 bg-white'>
                              {careerAnalyses.map((analysis) => (
                                <tr key={analysis.id}>
                                  <td className='whitespace-nowrap py-2 pl-4 pr-3 text-xs text-gray-900 sm:pl-6'>
                                    {analysis.person
                                      ? `${analysis.person.firstname || ''} ${analysis.person.lastname || ''}`.trim() ||
                                        analysis.person.email ||
                                        'Unbekannt'
                                      : 'Unbekannt'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {analysis.status || 'N/A'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {analysis.language || 'N/A'}
                                  </td>
                                  <td className='whitespace-nowrap px-3 py-2 text-xs text-gray-500'>
                                    {analysis.createdAt
                                      ? new Date(
                                          analysis.createdAt
                                        ).toLocaleDateString()
                                      : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className='mt-2 text-sm text-gray-500'>
                          Keine CareerAnalysen gefunden.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label
                    htmlFor='target_partner_ids'
                    className='block text-sm font-medium leading-6 text-gray-900'
                  >
                    Ziel Partner IDs (kommagetrennt)
                  </label>
                  <div className='mt-2 flex'>
                    <Field
                      type='text'
                      name='target_partner_ids'
                      id='target_partner_ids'
                      className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 px-3'
                      placeholder='Partner IDs eingeben (z.B. partner1, partner2, partner3)'
                    />
                    <Button.PrimaryLG
                      text='Laden'
                      className='ml-4'
                      onClick={() => fetchPartners(values.target_partner_ids)}
                      disabled={!values.target_partner_ids}
                    />
                  </div>
                  {errors.target_partner_ids && touched.target_partner_ids ? (
                    <div className='text-sm text-red-600 mt-1'>
                      {errors.target_partner_ids}
                    </div>
                  ) : null}
                </div>

                {partnersData && partnersData.length > 0 && (
                  <div className='bg-gray-50 p-4 rounded-md'>
                    <h3 className='text-base font-semibold leading-6 text-gray-900'>
                      Partner Details ({partnersData.length} Partner)
                    </h3>
                    <div className='mt-2 space-y-4'>
                      {partnersData.map((partner, index) => (
                        <div
                          key={partner.id}
                          className='border-b border-gray-200 pb-4 last:border-b-0'
                        >
                          <h4 className='text-sm font-medium text-gray-700 mb-2'>
                            Partner {index + 1}
                          </h4>
                          <dl className='grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-3'>
                            <div className='sm:col-span-1'>
                              <dt className='text-xs font-medium text-gray-500'>
                                ID
                              </dt>
                              <dd className='mt-1 text-xs text-gray-900'>
                                {partner.id}
                              </dd>
                            </div>
                            <div className='sm:col-span-1'>
                              <dt className='text-xs font-medium text-gray-500'>
                                Name
                              </dt>
                              <dd className='mt-1 text-xs text-gray-900'>
                                {partner.name}
                              </dd>
                            </div>
                            <div className='sm:col-span-1'>
                              <dt className='text-xs font-medium text-gray-500'>
                                Display Name
                              </dt>
                              <dd className='mt-1 text-xs text-gray-900'>
                                {partner.display_name}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className='flex justify-end'>
                  <Button.PrimaryXL
                    text={isCopying ? 'Kopiere...' : 'Kopieren'}
                    type='submit'
                    isLoading={isCopying}
                    disabled={
                      isSubmitting ||
                      !careerData ||
                      !partnersData ||
                      partnersData.length === 0 ||
                      partnersData.some(
                        (partner) => careerData.partner_id === partner.id
                      )
                    }
                  />
                </div>
              </Form>
            )}
          </Formik>

          {copyComplete && (
            <div className='mt-6 bg-green-50 p-4 rounded-md'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-green-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-green-800'>
                    Kopieren erfolgreich
                  </h3>
                  <div className='mt-2 text-sm text-green-700'>
                    <ul className='list-disc space-y-1 pl-5'>
                      <li>
                        {copyStats.positions} Stellen wurden erfolgreich kopiert
                      </li>
                      <li>
                        {copyStats.totalCareerAnalyses} CareerAnalysen wurden
                        insgesamt kopiert
                      </li>
                      <li>
                        {copyStats.totalPersons} Personen wurden insgesamt
                        kopiert
                      </li>
                      {copyStats.partnerResults &&
                        copyStats.partnerResults.length > 0 && (
                          <li className='mt-2'>
                            <div className='ml-2'>
                              <p className='font-medium mb-1'>
                                Details pro Partner:
                              </p>
                              <ul className='list-disc space-y-1 pl-5 text-xs'>
                                {copyStats.partnerResults.map(
                                  (result, index) => (
                                    <li key={result.partnerId || index}>
                                      {result.error ? (
                                        <span className='text-red-600'>
                                          {result.partnerName} (
                                          {result.partnerId}): Fehler -{' '}
                                          {result.error}
                                        </span>
                                      ) : (
                                        <span>
                                          {result.partnerName} (
                                          {result.partnerId}):{' '}
                                          {result.copiedAnalyses} Analysen,{' '}
                                          {result.copiedPersons} Personen
                                        </span>
                                      )}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </li>
                        )}
                    </ul>

                    {exportData && (
                      <div className='mt-4 flex'>
                        <button
                          type='button'
                          onClick={downloadExportData}
                          className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5 mr-2'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                            />
                          </svg>
                          Backup-Daten herunterladen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CopyPosition
